import { create } from 'zustand';
import type { ThreadMessage } from '@assistant-ui/react';
import type { AgenthubInterrupt } from './agenthubInterrupts';
import { markLastAssistantErrored } from './nativeThreadMessages';
import type { NativeEvent } from './nativeStream';

export type AgenthubJsonValue =
  | string
  | number
  | boolean
  | null
  | { readonly [key: string]: AgenthubJsonValue }
  | readonly AgenthubJsonValue[];
export type AgenthubState = { readonly [key: string]: AgenthubJsonValue };

export type AgenthubRuntimeKey = string;

export type AgenthubRuntimeSession = {
  agentId: string;
  threadId: string | null;
  messages: ThreadMessage[];
  isRunning: boolean;
  interrupts: AgenthubInterrupt[];
  state: AgenthubState;
  hydratedSignature: string | null;
  // A HITL interrupt's own run expired server-side (its wait-for-answer
  // timeout fired) before the user answered it — distinct from a normal
  // interrupt, which is always answerable. Cleared on the next new turn.
  runExpired: boolean;
  nativeEvents: NativeEvent[];
  connectionId: string | null;
  runStartedAt: number | null;
};

type AgenthubRuntimeStore = {
  runtimeGeneration: number;
  sessions: Record<AgenthubRuntimeKey, AgenthubRuntimeSession>;
  ensureSession: (key: AgenthubRuntimeKey, agentId: string, threadId: string | null) => void;
  hydrateSession: (
    key: AgenthubRuntimeKey,
    agentId: string,
    threadId: string | null,
    messages: ThreadMessage[],
    signature: string,
    pendingInterrupt: AgenthubInterrupt | null,
  ) => void;
  setMessages: (key: AgenthubRuntimeKey, messages: ThreadMessage[]) => void;
  // Attaches a curated error message to the last assistant message (or
  // creates one) for failures that happen outside the SSE event stream —
  // e.g. the connection itself broke and couldn't be recovered. Always pass
  // an already-curated, user-safe string (getUserErrorMessage), never a raw
  // caught error's .message.
  setMessageError: (key: AgenthubRuntimeKey, errorMessage: string) => void;
  setRunning: (key: AgenthubRuntimeKey, isRunning: boolean) => void;
  setState: (key: AgenthubRuntimeKey, state: AgenthubState) => void;
  setInterrupts: (key: AgenthubRuntimeKey, interrupts: AgenthubInterrupt[]) => void;
  setRunExpired: (key: AgenthubRuntimeKey, runExpired: boolean) => void;
  setNativeEvent: (key: AgenthubRuntimeKey, event: NativeEvent) => boolean;
  acquireConnection: (key: AgenthubRuntimeKey, connectionId: string) => boolean;
  releaseConnection: (key: AgenthubRuntimeKey, connectionId: string) => void;
  setRunStarted: (key: AgenthubRuntimeKey, startedAt: number | null) => void;
  appendMessage: (key: AgenthubRuntimeKey, message: ThreadMessage) => void;
};

export function agenthubRuntimeKey(agentId: string, threadId: string | null): AgenthubRuntimeKey {
  return `${agentId}:${threadId ?? 'new'}`;
}

function createSession(agentId: string, threadId: string | null): AgenthubRuntimeSession {
  return {
    agentId,
    threadId,
    messages: [],
    isRunning: false,
    interrupts: [],
    state: {},
    hydratedSignature: null,
    runExpired: false,
    nativeEvents: [],
    connectionId: null,
    runStartedAt: null,
  };
}

export const useAgenthubRuntimeStore = create<AgenthubRuntimeStore>((set) => ({
  runtimeGeneration: 0,
  sessions: {},
  ensureSession: (key, agentId, threadId) =>
    set((store) => {
      if (store.sessions[key]) return store;
      return { sessions: { ...store.sessions, [key]: createSession(agentId, threadId) } };
    }),
  hydrateSession: (key, agentId, threadId, messages, signature, pendingInterrupt) =>
    set((store) => {
      const current = store.sessions[key] ?? createSession(agentId, threadId);
      // A newly accepted thread can mount while its live stream is still
      // being projected. Its optimistic/live messages must win over the
      // stale empty transcript returned by the first hydration request.
      // Once a session has any locally-populated messages, never let a
      // hydration replace them outright: client message ids are locally
      // generated UUIDs (see appendMessageToThreadMessage/currentAssistant)
      // that are never reconciled with the server's persisted row id, so a
      // DB-sourced hydration's signature essentially never matches — without
      // this guard that mismatch alone was enough to silently swap out an
      // already-correct transcript (visible flicker) any time hydration
      // re-ran, e.g. after a background refetch.
      //
      // Deliberately NOT gated on `current.isRunning` alone: on a refresh,
      // the reattach effect can set isRunning=true before this hydration call
      // resolves (its query settles on a different render). Blocking on
      // isRunning while messages is still empty permanently locked the
      // transcript blank for that mount -- the persisted transcript must be
      // allowed in as long as there's nothing local yet to protect.
      if (current.hydratedSignature === signature || current.messages.length > 0) return store;
      return {
        sessions: {
          ...store.sessions,
          [key]: {
            ...current,
            agentId,
            threadId,
            messages,
            interrupts: pendingInterrupt ? [pendingInterrupt] : [],
            hydratedSignature: signature,
          },
        },
      };
    }),
  setMessages: (key, messages) =>
    set((store) => ({
      sessions: {
        ...store.sessions,
        [key]: { ...(store.sessions[key] ?? createSession('', null)), messages },
      },
    })),
  setMessageError: (key, errorMessage) =>
    set((store) => {
      const current = store.sessions[key] ?? createSession('', null);
      return {
        sessions: {
          ...store.sessions,
          [key]: { ...current, messages: markLastAssistantErrored(current.messages, errorMessage) },
        },
      };
    }),
  setRunning: (key, isRunning) =>
    set((store) => {
      const current = store.sessions[key] ?? createSession('', null);
      return {
        sessions: {
          ...store.sessions,
          // A new run starting is a fresh attempt — clear any stale expiry
          // from a previous one instead of leaving the "restart" banner up.
          [key]: { ...current, isRunning, runExpired: isRunning ? false : current.runExpired },
        },
      };
    }),
  setState: (key, state) =>
    set((store) => ({
      sessions: {
        ...store.sessions,
        [key]: { ...(store.sessions[key] ?? createSession('', null)), state },
      },
    })),
  setInterrupts: (key, interrupts) =>
    set((store) => ({
      sessions: {
        ...store.sessions,
        [key]: { ...(store.sessions[key] ?? createSession('', null)), interrupts },
      },
    })),
  setRunExpired: (key, runExpired) =>
    set((store) => ({
      sessions: {
        ...store.sessions,
        [key]: { ...(store.sessions[key] ?? createSession('', null)), runExpired },
      },
    })),
  setNativeEvent: (key, event) => {
    let accepted = false;
    set((store) => {
      const current = store.sessions[key] ?? createSession('', null);
      if (current.nativeEvents.some((seen) => seen.event_id === event.event_id
        || (seen.run_id === event.run_id && seen.sequence === event.sequence))) return store;
      accepted = true;
      return {
        sessions: {
          ...store.sessions,
          [key]: { ...current, nativeEvents: [...current.nativeEvents, event] },
        },
      };
    });
    return accepted;
  },
  acquireConnection: (key, connectionId) => {
    let acquired = false;
    set((store) => {
      const current = store.sessions[key] ?? createSession('', null);
      if (current.connectionId) return store;
      acquired = true;
      return { sessions: { ...store.sessions, [key]: { ...current, connectionId } } };
    });
    return acquired;
  },
  releaseConnection: (key, connectionId) =>
    set((store) => {
      const current = store.sessions[key] ?? createSession('', null);
      if (current.connectionId !== connectionId) return store;
      return { sessions: { ...store.sessions, [key]: { ...current, connectionId: null } } };
    }),
  setRunStarted: (key, startedAt) =>
    set((store) => {
      const current = store.sessions[key] ?? createSession('', null);
      return { sessions: { ...store.sessions, [key]: { ...current, runStartedAt: startedAt } } };
    }),
  appendMessage: (key, message) =>
    set((store) => {
      const current = store.sessions[key] ?? createSession('', null);
      return {
        sessions: {
          ...store.sessions,
          [key]: { ...current, messages: [...current.messages, message] },
        },
      };
    }),
}));

/** Clears every in-memory transcript so a workspace reset cannot remount stale agent state. */
export function clearAgenthubRuntimeSessions(): void {
  useAgenthubRuntimeStore.setState((store) => ({
    runtimeGeneration: store.runtimeGeneration + 1,
    sessions: {},
  }));
}
